// FINAL VERSION — PRODUCT ID ERRORS FIXED PERMANENTLY

import React, { useState } from 'react';
import {
  Row,
  Col,
  Form,
  Button,
  Table,
  Alert,
  ListGroup,
  Pagination,
  Container,
  ProgressBar,
} from 'react-bootstrap';
import {
  useGetTransfersQuery,
  useUpdateTransferMutation,
} from '../../slices/transfersApiSlice';
import { useGetProductsQuery } from '../../slices/productsApiSlice';
import Loader from '../../components/Loader';
import Message from '../../components/Message';
import { toast } from 'react-toastify';

const TransfersSearchReplaceScreen = () => {
// ------------------------------
// STATE
// ------------------------------
const [searchTerm, setSearchTerm] = useState('');
const [replacementName, setReplacementName] = useState('');
const [selectedProduct, setSelectedProduct] = useState(null);
const [previewResults, setPreviewResults] = useState([]);
const [selectedItems, setSelectedItems] = useState({});
const [selectAll, setSelectAll] = useState(false);
const [isReplacing, setIsReplacing] = useState(false);
const [progress, setProgress] = useState(0);
const [diffPreview, setDiffPreview] = useState([]);
const [pageNumber, setPageNumber] = useState(1);
const [focusedBox, setFocusedBox] = useState('search');
const [selectedMonth, setSelectedMonth] = useState('');

// ------------------------------
// DATA FETCH
// ------------------------------
const { data: transfersData, isLoading, error } = useGetTransfersQuery();
const {
  data: productsData = { products: [], pages: 1 },
  isLoading: loadingProducts,
  error: errorProducts,
} = useGetProductsQuery({ pageNumber });

const [updateTransfer] = useUpdateTransferMutation();

// ------------------------------
// NORMALIZE TRANSFERS (auto-repair)
// ------------------------------
const normalizeTransfer = (transfer) => {
  const rawItems = transfer.transferItems || transfer.items || [];

  const normalizedItems = rawItems.map((item) => {
    const originalId = item.product || item._id || null;

    const productExists = productsData.products.some(
      (p) => p._id === originalId
    );

    let repairedId = originalId;

    if (!productExists) {
      const match = productsData.products.find(
        (p) =>
          p.name.toLowerCase() === (item.name || '').toLowerCase()
      );
      if (match) repairedId = match._id;
    }

    return {
      product: repairedId,
      name: item.name || '',
      qty: item.qty,
      price: item.price,
      images: item.images || [],
      repaired: repairedId !== originalId,
      productExists,
    };
  });

  return {
    ...transfer,
    transferItems: normalizedItems,
    customerDetails: transfer.customerDetails || {},
    status: transfer.status || 'completed',
    note: transfer.note || '',
  };
};

const normalizedTransfers = transfersData?.transfers?.map(normalizeTransfer) || [];

// ------------------------------
// PRODUCT SELECTION
// ------------------------------
const handleProductSelect = (product) => {
  if (focusedBox === 'search') {
    setSearchTerm(product.name);
  } else {
    setReplacementName(product.name);
    setSelectedProduct(product);
  }
  setPreviewResults([]);
  setSelectedItems({});
  setSelectAll(false);
};

// ------------------------------
// SEARCH TRANSFERS
// ------------------------------
const handleSearch = () => {
  if (!searchTerm.trim()) return;

  const matches = normalizedTransfers.flatMap((transfer) =>
    transfer.transferItems
      .filter((item) =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .map((item, index) => ({
        transferId: transfer._id,
        itemIndex: index,
        uniqueId: `${transfer._id}-${index}`,
        originalName: item.name,
        qty: item.qty,
        price: item.price,
        date: transfer.date,
        repaired: item.repaired,
        productExists: item.productExists,
      }))
  );

  setPreviewResults(matches || []);
  setSelectedItems({});
  setSelectAll(false);
};

// ------------------------------
// MONTH FILTERING
// ------------------------------
const monthFilteredResults = selectedMonth
  ? previewResults.filter((item) => {
      const d = new Date(item.date);
      return d.getMonth() + 1 === Number(selectedMonth);
    })
  : previewResults;

// ------------------------------
// SELECT ALL (GLOBAL)
// ------------------------------
const toggleSelectAll = () => {
  const newState = !selectAll;
  setSelectAll(newState);

  if (newState) {
    const all = {};
    monthFilteredResults.forEach((item) => {
      all[item.uniqueId] = true;
    });
    setSelectedItems(all);
  } else {
    setSelectedItems({});
  }
};

// ------------------------------
// SELECT SINGLE ROW
// ------------------------------
const toggleSelectItem = (uniqueId) => {
  setSelectedItems((prev) => {
    const updated = { ...prev };
    if (updated[uniqueId]) delete updated[uniqueId];
    else updated[uniqueId] = true;
    return updated;
  });
};

// ------------------------------
// DIFF PREVIEW
// ------------------------------
const buildDiffPreview = (replacementProduct) => {
  const diffs = monthFilteredResults
    .filter((item) => selectedItems[item.uniqueId])
    .map((item) => ({
      originalName: item.originalName,
      originalPrice: item.price,
      newName: replacementProduct.name,
      newPrice: replacementProduct.price,
    }));

  setDiffPreview(diffs);
};

// ------------------------------
// REPLACE SELECTED ITEMS (FULL ITEM OBJECTS)
// ------------------------------
const handleReplace = async () => {
  if (!replacementName.trim()) return toast.error('Replacement name required');
  if (!searchTerm.trim()) return toast.error('Search term required');

  const replacementProduct =
    productsData.products.find(
      (p) => p.name.toLowerCase() === replacementName.toLowerCase()
    ) ||
    productsData.products.find((p) =>
      p.name.toLowerCase().includes(replacementName.toLowerCase())
    );

  if (!replacementProduct) {
    return toast.error(`Product "${replacementName}" not found`);
  }

  const selectedIds = Object.keys(selectedItems);
  if (selectedIds.length === 0) {
    return toast.info('No items selected for replacement');
  }

  buildDiffPreview(replacementProduct);
  setIsReplacing(true);

  try {
    const groupedByTransfer = {};

    monthFilteredResults.forEach((item) => {
      if (selectedItems[item.uniqueId]) {
        if (!groupedByTransfer[item.transferId]) groupedByTransfer[item.transferId] = [];
        groupedByTransfer[item.transferId].push(item.itemIndex);
      }
    });

    const totalTransfersToUpdate = Object.keys(groupedByTransfer).length;
    let processed = 0;

    for (const transfer of normalizedTransfers) {
      const selectedIndexes = groupedByTransfer[transfer._id];
      if (!selectedIndexes) continue;

      const updatedItems = transfer.transferItems.map((item, index) => {
        const isSelected = selectedIndexes.includes(index);

        return isSelected
          ? {
              product: replacementProduct._id,
              name: replacementProduct.name,
              price: replacementProduct.price,
              qty: item.qty,
              images: replacementProduct.images || [],
            }
          : {
              product: item.product,
              name: item.name,
              price: item.price,
              qty: item.qty,
              images: item.images || [],
            };
      });

      await updateTransfer({
        transferId: transfer._id,
        transferItems: updatedItems,
        customerDetails: transfer.customerDetails,
        note: transfer.note,
        status: transfer.status,
        date: transfer.date,
      }).unwrap();

      processed += 1;
      setProgress(Math.round((processed / totalTransfersToUpdate) * 100));
    }

    toast.success(`Replaced selected items with "${replacementProduct.name}"`);
    setPreviewResults([]);
    setSelectedItems({});
    setSelectAll(false);
    setDiffPreview([]);
  } catch (err) {
    toast.error(err?.data?.message || 'Update failed');
  } finally {
    setIsReplacing(false);
    setProgress(0);
  }
};

// ------------------------------
// FILTER PRODUCTS LIST
// ------------------------------
const activeFilter =
  focusedBox === 'search' ? searchTerm : replacementName;

const filteredProducts = productsData.products.filter((p) =>
  p.name.toLowerCase().includes(activeFilter.toLowerCase())
);

return (
  <Container fluid>
    <Row>
      {/* LEFT SIDE: PRODUCT LIST + MONTH FILTER + ACTION BUTTONS */}
      <Col md={3}>
        <h5 className="mb-3">Product List</h5>

        {/* Month Selector */}
        <Form.Select
          className="mb-3"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
        >
          <option value="">Filter by Month (Optional)</option>
          {Array.from({ length: 12 }).map((_, i) => (
            <option key={i + 1} value={i + 1}>
              {new Date(0, i).toLocaleString('default', { month: 'long' })}
            </option>
          ))}
        </Form.Select>

        {/* Month Actions */}
        <div className="d-grid gap-2 mb-3">
          <Button
            variant="secondary"
            disabled={!selectedMonth || monthFilteredResults.length === 0}
            onClick={() => {
              const all = {};
              monthFilteredResults.forEach((item) => {
                all[item.uniqueId] = true;
              });
              setSelectedItems(all);
              setSelectAll(false);
              toast.info("All rows for this month selected");
            }}
          >
            Select All for This Month
          </Button>

          <Button
            variant="outline-danger"
            disabled={!selectedMonth}
            onClick={() => {
              setSelectedMonth('');
              toast.info("Month filter cleared");
            }}
          >
            Clear Month Filter
          </Button>

          <Button
            variant="outline-warning"
            disabled={monthFilteredResults.length === 0}
            onClick={() => {
              const repaired = {};
              monthFilteredResults.forEach((item) => {
                if (!item.productExists && item.repaired) {
                  repaired[item.uniqueId] = true;
                }
              });
              setSelectedItems(repaired);
              toast.success("All auto‑repairable items selected");
            }}
          >
            Fix All Missing Product IDs
          </Button>
        </div>

        {/* Product List */}
        {loadingProducts ? (
          <Loader />
        ) : errorProducts ? (
          <Message variant="danger">
            {errorProducts?.data?.message || 'Failed to load products'}
          </Message>
        ) : (
          <>
            <ListGroup>
              {filteredProducts.map((product) => (
                <ListGroup.Item
                  key={product._id}
                  action
                  onClick={() => handleProductSelect(product)}
                >
                  {product.name} — D{product.price?.toFixed(2)}
                </ListGroup.Item>
              ))}
            </ListGroup>

            <Pagination className="mt-3">
              {[...Array(productsData.pages).keys()].map((x) => (
                <Pagination.Item
                  key={x + 1}
                  active={x + 1 === pageNumber}
                  onClick={() => setPageNumber(x + 1)}
                >
                  {x + 1}
                </Pagination.Item>
              ))}
            </Pagination>
          </>
        )}
      </Col>

      {/* RIGHT SIDE */}
      <Col md={9}>
        <Row className="mb-3">
          <Col><h1>Transfers Search & Replace</h1></Col>
        </Row>

        {/* Search + Replace Inputs */}
        <Row className="mb-3">
          <Col md={4}>
            <Form.Control
              type="text"
              placeholder="Search for product name in transfers"
              value={searchTerm}
              onFocus={() => setFocusedBox('search')}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Col>

          <Col md={4}>
            <Form.Control
              type="text"
              placeholder="Replacement product name"
              value={replacementName}
              onFocus={() => setFocusedBox('replace')}
              onChange={(e) => {
                setReplacementName(e.target.value);
                setSelectedProduct(null);
              }}
            />
          </Col>

          <Col md={4}>
            <Button
              variant="primary"
              onClick={handleSearch}
              className="me-2"
              disabled={!searchTerm.trim() || isReplacing}
            >
              Preview Matches
            </Button>
            <Button
              variant="success"
              onClick={handleReplace}
              disabled={!replacementName.trim() || isReplacing}
            >
              Replace Selected
            </Button>
          </Col>
        </Row>

        {selectedProduct && (
          <Alert variant="info">
            Selected replacement: <strong>{selectedProduct.name}</strong> — D
            {selectedProduct.price.toFixed(2)}
          </Alert>
        )}

        {diffPreview.length > 0 && (
          <Alert variant="secondary">
            <strong>Preview changes:</strong>
            <ul className="mb-0">
              {diffPreview.map((d, idx) => (
                <li key={idx}>
                  {d.originalName} (D{d.originalPrice.toFixed(2)}) →{' '}
                  {d.newName} (D{d.newPrice.toFixed(2)})
                </li>
              ))}
            </ul>
          </Alert>
        )}

        {isReplacing && (
          <ProgressBar
            now={progress}
            label={`${progress}%`}
            className="mb-3"
          />
        )}

        {/* Monthly Summary Panel */}
        {selectedMonth && (
          <Alert variant="dark" className="mb-3">
            <h5>
              Summary for{' '}
              {new Date(0, selectedMonth - 1).toLocaleString('default', {
                month: 'long',
              })}
            </h5>
            <p>Total Rows: {monthFilteredResults.length}</p>
            <p>
              Missing Product IDs:{' '}
              {
                monthFilteredResults.filter(
                  (i) => !i.productExists && !i.repaired
                ).length
              }
            </p>
            <p>
              Auto‑Repaired:{' '}
              {monthFilteredResults.filter((i) => i.repaired).length}
            </p>
          </Alert>
        )}

        {/* Preview Table */}
        {isLoading ? (
          <Loader />
        ) : error ? (
          <Message variant="danger">
            {error?.data?.message || 'Error loading transfers'}
          </Message>
        ) : monthFilteredResults.length === 0 ? (
          <Alert variant="warning">
            {selectedMonth
              ? 'No matching items found for this month.'
              : 'No matching items found.'}
          </Alert>
        ) : (
          <>
            <Form.Check
              type="checkbox"
              label="Select All"
              checked={selectAll}
              onChange={toggleSelectAll}
              className="mb-2"
            />

            <Table striped bordered hover responsive className="table-sm">
              <thead>
                <tr>
                  <th></th>
                  <th>DATE</th>
                  <th>ORIGINAL NAME</th>
                  <th>QTY</th>
                  <th>PRICE</th>
                  <th>STATUS</th>
                </tr>
              </thead>
              <tbody>
                {monthFilteredResults.map((item) => (
                  <tr key={item.uniqueId}>
                    <td>
                      <Form.Check
                        type="checkbox"
                        checked={!!selectedItems[item.uniqueId]}
                        onChange={() => toggleSelectItem(item.uniqueId)}
                      />
                    </td>
                    <td>{new Date(item.date).toLocaleDateString()}</td>
                    <td>{item.originalName}</td>
                    <td>{item.qty}</td>
                    <td>D{item.price.toFixed(2)}</td>
                    <td>
                      {!item.productExists && !item.repaired && (
                        <span style={{ color: 'red', fontWeight: 'bold' }}>
                          Missing Product ID
                        </span>
                      )}
                      {item.repaired && (
                        <span style={{ color: 'green', fontWeight: 'bold' }}>
                          Auto‑Repaired
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </Col>
    </Row>
  </Container>
);

}; 
export default TransfersSearchReplaceScreen;
